import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldCheck, RefreshCcw, RotateCcw, XCircle } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function BillingPortal() {
    const { user, refresh } = useAuth();
    const navigate = useNavigate();
    const [sub, setSub] = useState(null);
    const [plans, setPlans] = useState([]);
    const [portal, setPortal] = useState(null);
    const [invoices, setInvoices] = useState(null); // null=loading, []=empty
    const [invError, setInvError] = useState(false);
    const [busy, setBusy] = useState(null); // "cancel" | "resume" | "change:<plan>"

    async function load() {
        const [subRes, plansRes] = await Promise.all([
            api.get("/billing/subscription"),
            api.get("/billing/plans"),
        ]);
        setSub(subRes.data);
        setPlans(plansRes.data.items);
        api.get("/billing/portal")
            .then(({ data }) => setPortal(data))
            .catch(() => {});
        api.get("/billing/invoices")
            .then(({ data }) => setInvoices(data.items || []))
            .catch(() => { setInvError(true); setInvoices([]); });
    }

    useEffect(() => {
        load().catch(() => toast.error("Could not load subscription"));
    }, []);

    async function scheduleCancel() {
        try {
            setBusy("cancel");
            const { data } = await api.post("/billing/subscription/cancel", {});
            setSub(data);
            toast.success("Cancellation scheduled at period end");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not cancel");
        } finally {
            setBusy(null);
        }
    }

    async function resume() {
        try {
            setBusy("resume");
            const { data } = await api.post("/billing/subscription/resume", {});
            setSub(data);
            toast.success("Subscription resumed");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not resume");
        } finally {
            setBusy(null);
        }
    }

    async function changePlan(planKey, cycle = "monthly") {
        try {
            setBusy(`change:${planKey}`);
            const { data } = await api.post("/billing/subscription/change-plan", {
                plan_key: planKey,
                cycle,
            });
            setSub(data);
            await refresh();
            toast.success(`Switched to ${data.plan_name}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not switch plan");
        } finally {
            setBusy(null);
        }
    }

    if (!sub) {
        return (
            <div className="container-edit py-20 flex justify-center">
                <Loader2 className="animate-spin" color="#0D1B3E" />
            </div>
        );
    }

    const isPaid = sub.plan_key !== "free";
    const renewsOn = new Date(sub.current_period_end).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
    });

    return (
        <div className="container-edit py-10 lg:py-16 max-w-4xl" data-testid="billing-portal">
            <div className="label-eyebrow">Billing portal</div>
            <h1 className="font-display text-3xl lg:text-4xl mt-2 tracking-tight">
                Manage your subscription
            </h1>
            <p className="mt-3 text-[#0D1B3E]/70 max-w-xl">
                Review your plan, renewal date and usage here. Payment methods and
                invoices are managed on our secure hosted payment portal.
            </p>

            {/* Current plan card */}
            <div className="mt-10 card-flat p-8" data-testid="billing-portal-summary">
                <div className="flex items-start justify-between gap-6 flex-wrap">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.22em] text-[#0D1B3E]/55">
                            Current plan
                        </div>
                        <div
                            className="font-display text-3xl tracking-tight mt-1"
                            data-testid="billing-portal-plan-name"
                        >
                            {sub.plan_name}
                        </div>
                        <div className="mt-2 text-sm text-[#0D1B3E]/70 capitalize">
                            {sub.plan_key === "free"
                                ? "No active subscription"
                                : `Billed ${sub.cycle} · €${sub.last_amount_eur.toLocaleString()}`}
                        </div>
                    </div>
                    <StatusBadge status={sub.status} cancelAtEnd={sub.cancel_at_period_end} />
                </div>

                <hr className="gold-rule my-7" />

                <div className="grid gap-4 sm:grid-cols-2 text-sm">
                    <KV label="Customer" value={sub.provider_customer_id || "–"} />
                    <KV
                        label="Subscription ID"
                        value={sub.provider_subscription_id || "–"}
                        mono
                    />
                    <KV
                        label={sub.cancel_at_period_end ? "Access ends" : "Next renewal"}
                        value={sub.plan_key === "free" ? "–" : renewsOn}
                    />
                    <KV label="Signed in as" value={user?.email || "–"} />
                </div>

                <div className="mt-8 flex flex-wrap gap-3">
                    {isPaid && !sub.cancel_at_period_end && (
                        <button
                            type="button"
                            onClick={scheduleCancel}
                            disabled={busy === "cancel"}
                            className="btn-secondary"
                            data-testid="billing-portal-cancel"
                        >
                            {busy === "cancel" ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : (
                                <XCircle size={16} />
                            )}
                            Cancel subscription
                        </button>
                    )}
                    {isPaid && sub.cancel_at_period_end && (
                        <button
                            type="button"
                            onClick={resume}
                            disabled={busy === "resume"}
                            className="btn-primary"
                            data-testid="billing-portal-resume"
                        >
                            {busy === "resume" ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : (
                                <RotateCcw size={16} />
                            )}
                            Resume subscription
                        </button>
                    )}
                    <Link to="/app/pricing" className="btn-secondary" data-testid="billing-portal-plans">
                        View all plans
                    </Link>
                    {portal?.external && (
                        <a
                            href={portal.portal_url}
                            target="_blank"
                            rel="noreferrer"
                            className="btn-secondary"
                            data-testid="billing-portal-payment-methods"
                        >
                            Manage payment method &amp; invoices
                        </a>
                    )}
                </div>
            </div>

            {/* Switch plan */}
            {plans.length > 0 && (
                <div className="mt-14" data-testid="billing-portal-switch">
                    <div className="label-eyebrow">Change plan</div>
                    <h2 className="font-display text-2xl mt-1 tracking-tight">
                        Switch instantly, prorated on renewal.
                    </h2>
                    <div className="mt-6 grid gap-4 sm:grid-cols-2">
                        {plans.map((p) => {
                            const current = p.key === sub.plan_key;
                            const key = `change:${p.key}`;
                            return (
                                <div
                                    key={p.key}
                                    className="card-flat p-6 flex items-center justify-between gap-4"
                                    data-testid={`billing-portal-plan-${p.key}`}
                                >
                                    <div>
                                        <div className="font-display text-lg tracking-tight">
                                            {p.name}
                                        </div>
                                        <div className="text-xs uppercase tracking-[0.22em] text-[#0D1B3E]/55 mt-1">
                                            €{p.price_eur} / mo · {p.monthly_queries.toLocaleString()} credits
                                        </div>
                                    </div>
                                    {current ? (
                                        <span className="text-xs uppercase tracking-[0.22em] text-[#C9A84C] font-semibold">
                                            Current
                                        </span>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => changePlan(p.key, "monthly")}
                                            disabled={busy === key}
                                            className="btn-primary"
                                            data-testid={`billing-portal-switch-${p.key}`}
                                        >
                                            {busy === key ? (
                                                <Loader2 size={16} className="animate-spin" />
                                            ) : (
                                                <RefreshCcw size={16} />
                                            )}
                                            Switch
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="mt-10 card-flat p-6" data-testid="billing-portal-invoices">
                <div className="label-eyebrow">Payment history</div>
                {invoices === null ? (
                    <div className="mt-4 flex items-center gap-2 text-sm text-[#0D1B3E]/60" data-testid="invoices-loading">
                        <Loader2 size={15} className="animate-spin" /> Loading payment history…
                    </div>
                ) : invError ? (
                    <div className="mt-4 text-sm text-[#0D1B3E]/65" data-testid="invoices-error">
                        We couldn't load your payment history right now. Please refresh
                        or try again shortly.
                    </div>
                ) : invoices.length === 0 ? (
                    <div className="mt-4 text-sm text-[#0D1B3E]/60" data-testid="invoices-empty">
                        No payments yet. Once you subscribe, your receipts will appear here.
                    </div>
                ) : (
                    <table className="mt-4 w-full text-sm">
                        <thead>
                            <tr className="text-left border-b border-[#0D1B3E]/10">
                                <th className="py-2 pr-4 label-eyebrow">Date</th>
                                <th className="py-2 pr-4 label-eyebrow">Plan</th>
                                <th className="py-2 pr-4 label-eyebrow">Amount</th>
                                <th className="py-2 pr-4 label-eyebrow">Status</th>
                                <th className="py-2 label-eyebrow">Receipt</th>
                            </tr>
                        </thead>
                        <tbody>
                            {invoices.map((inv) => (
                                <tr key={inv.id} className="border-b border-[#0D1B3E]/5">
                                    <td className="py-2.5 pr-4 text-[#0D1B3E]/70">
                                        {inv.created_at ? new Date(inv.created_at).toLocaleDateString() : "–"}
                                    </td>
                                    <td className="py-2.5 pr-4 capitalize text-[#0D1B3E]/75">
                                        {inv.plan || "–"}
                                    </td>
                                    <td className="py-2.5 pr-4 font-medium">
                                        €{inv.amount.toLocaleString()}
                                    </td>
                                    <td className="py-2.5 pr-4 capitalize text-[#0D1B3E]/70">{inv.status}</td>
                                    <td className="py-2.5">
                                        {portal?.external ? (
                                            <a href={portal.portal_url} target="_blank" rel="noreferrer"
                                                className="text-xs underline text-[#0D1B3E]/65 hover:text-[#C9A84C]"
                                                data-testid={`invoice-receipt-${inv.id}`}>
                                                View receipt
                                            </a>
                                        ) : "–"}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
                {invoices?.length > 0 && portal?.external && (
                    <a href={portal.portal_url} target="_blank" rel="noreferrer"
                        className="mt-4 inline-block text-xs underline text-[#0D1B3E]/65 hover:text-[#C9A84C]">
                        Download VAT invoices on the payment portal
                    </a>
                )}
            </div>

            <div className="mt-14 flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-[#0D1B3E]/55">
                <ShieldCheck size={14} color="#C9A84C" />
                Payments processed securely. Your card details never touch our servers.
            </div>

            <button
                type="button"
                onClick={() => navigate("/app/profile")}
                className="mt-8 text-sm underline text-[#0D1B3E]/60 hover:text-[#0D1B3E]"
                data-testid="billing-portal-back"
            >
                Back to profile
            </button>
        </div>
    );
}

function StatusBadge({ status, cancelAtEnd }) {
    const label = cancelAtEnd
        ? "Cancels at period end"
        : status === "active"
          ? "Active"
          : status === "cancelled"
            ? "Cancelled"
            : "Expired";
    const cls = cancelAtEnd
        ? "bg-[#8B1E1E]/10 text-[#8B1E1E]"
        : status === "active"
          ? "bg-[#C9A84C]/15 text-[#8A6E1D]"
          : "bg-[#0D1B3E]/10 text-[#0D1B3E]/70";
    return (
        <span
            className={`text-[11px] uppercase tracking-[0.22em] font-semibold px-3 py-1 ${cls}`}
            data-testid="billing-portal-status"
        >
            {label}
        </span>
    );
}

function KV({ label, value, mono }) {
    return (
        <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#0D1B3E]/55">
                {label}
            </div>
            <div className={`mt-1 ${mono ? "font-mono text-xs break-all" : ""}`}>{value}</div>
        </div>
    );
}
